import { HalfEvent, MatchEvent, MatchPhone } from "refsix-js-models";
import {
    isStoppingClock,
    millisToMins,
    periodOf,
    playedMillisAt,
    playedMillisInPeriod,
    stoppingClockMinuteInPeriod,
    stoppingClockMinuteStart,
} from "refsix-core";
import { formatDuration } from "./formatDuration";

// REFSIX-745: minutes for a match created with a stopping clock. Recorded
// minutes are untouched; prior stoppages in the period are deducted from what
// is shown, and every screen that shows an event's minute goes through here.

type SplitMinute = { minute: number; added: number };

// A 1-based minute of the period as match minute plus added time, e.g. 45+2
function splitAtScheduledLength(
    minuteInPeriod: number,
    period: HalfEvent
): SplitMinute {
    const scheduled = millisToMins(period.length);
    return minuteInPeriod > scheduled
        ? {
              minute: period.minuteOfPlay + scheduled,
              added: minuteInPeriod - scheduled,
          }
        : { minute: period.minuteOfPlay + minuteInPeriod, added: 0 };
}

function stoppingClockMinute(
    event: MatchEvent,
    period: HalfEvent,
    events: MatchEvent[]
): SplitMinute {
    return splitAtScheduledLength(
        stoppingClockMinuteInPeriod(event, period.timestamp, events),
        period
    );
}

export function stoppingClockDisplayMinute(
    event: MatchEvent,
    period: HalfEvent,
    events: MatchEvent[]
): string {
    const { minute, added } = stoppingClockMinute(event, period, events);
    return added ? `${minute}'+${added}'` : `${minute}'`;
}

// The playing period whose stopping clock an event's minute is shown on, if any
export function stoppingClockPeriodOf(
    event: MatchEvent,
    match: MatchPhone,
    events: MatchEvent[]
): HalfEvent | undefined {
    const period = isStoppingClock(match) ? periodOf(event, events) : undefined;
    return period?.playing ? period : undefined;
}

// The minute an event is shown at, on any screen, for either clock
export function eventDisplayMinute(
    event: MatchEvent,
    match: MatchPhone,
    events: MatchEvent[] = Object.values(match.matchEvents ?? {})
): string {
    const period = stoppingClockPeriodOf(event, match, events);
    if (period) {
        return stoppingClockDisplayMinute(event, period, events);
    }
    return event.additionalTime
        ? `${event.minuteOfPlay}'+${event.additionalTime}'`
        : `${event.minuteOfPlay}'`;
}

// How long the clock ran in a period once its stoppages and any suspended time
// (REFSIX-647) are taken out. A period that hasn't ended yet is assumed to run
// its scheduled length, as computeHalfInfo does.
function clockMillisInPeriod(period: HalfEvent, events: MatchEvent[]) {
    return period.endTime
        ? playedMillisInPeriod(period, events, true)
        : period.length;
}

// The period-end row: how long the clock ran, e.g. "45:00'"
export function stoppingClockPeriodEndDisplay(
    period: HalfEvent,
    events: MatchEvent[]
): string {
    const playedSeconds = Math.floor(
        clockMillisInPeriod(period, events) / 1000
    );
    return `${formatDuration(Math.max(playedSeconds, 0))}'`;
}

// Edit-event minute dropdown values, in the running clock's "45" / "45+2" form

function minuteValue({ minute, added }: SplitMinute): string {
    return added ? `${minute}+${added}` : `${minute}`;
}

// Every minute the stopping clock showed in the period
export function stoppingClockMinuteOptions(
    period: HalfEvent,
    events: MatchEvent[]
): string[] {
    const minutes = Math.max(
        millisToMins(period.length),
        Math.ceil(clockMillisInPeriod(period, events) / 60000)
    );
    return Array.from({ length: minutes }, (_, index) =>
        minuteValue(splitAtScheduledLength(index + 1, period))
    );
}

export function stoppingClockMinuteValue(
    event: MatchEvent,
    period: HalfEvent,
    events: MatchEvent[]
): string {
    return minuteValue(stoppingClockMinute(event, period, events));
}

// Where a minute picked from the dropdown puts the event: the start of that
// minute on the stopping clock. Its recorded minute stays the elapsed one less
// any suspended time (REFSIX-647), the same as the watch records.
export function stoppingClockEventTiming(
    value: string,
    period: HalfEvent,
    events: MatchEvent[]
): { timestamp: number; minuteOfPlay: number; additionalTime?: number } {
    const [minute, added = 0] = value.split("+").map(Number);
    const timestamp = stoppingClockMinuteStart(
        minute - period.minuteOfPlay + added,
        period,
        events
    );
    const elapsed = splitAtScheduledLength(
        Math.floor(playedMillisAt(timestamp, period, events) / 60000) + 1,
        period
    );
    return {
        timestamp,
        minuteOfPlay: elapsed.minute,
        additionalTime: elapsed.added || undefined,
    };
}
